/**
 * The camera bound: keeps the board reachable from any pan/zoom sequence. {@link CombatStage} owns
 * the pointer wiring and the `view` state itself (the wheel-zoom, pan-drag, initial-center, and
 * camera-focus writers); everything *decided* about where the camera is allowed to land lives here
 * so it is unit-tested without a canvas — the same split {@link combatPan} makes for the pan button
 * rules.
 *
 * **The invariant:** the viewport's centre point, expressed in world coordinates, must lie
 * inside the board rect — `0 <= wx <= board.width` and `0 <= wy <= board.height`, where
 * `wx = (viewport.width / 2 - view.x) / view.scale` (and likewise for y). Solving for `view.x`:
 *
 *   `view.x = viewport.width / 2 - wx * view.scale`, `wx ∈ [0, board.width]`
 *   ⟺ `view.x ∈ [viewport.width / 2 - board.width * view.scale, viewport.width / 2]`
 *
 * so the clamp is a plain interval clamp on `x` and (symmetrically) `y`. The interval is never
 * empty (`board.width * scale >= 0`), which is why one branch-free clamp is correct in BOTH size
 * relations — board bigger than the viewport, and board smaller than the viewport after a zoom-out —
 * with no special-casing either way. `scale` is not this function's business: the wheel writer
 * already clamps it to `[MIN_SCALE, MAX_SCALE]` before the view update reaches here, so `scale`
 * passes through untouched.
 *
 * **Margin is zero.** The bound is the bare board rect — no configurable inset. If the feel ever
 * wants breathing room, that is one constant to introduce later; it is not part of this decision.
 *
 * **Two things this clamp deliberately does NOT smooth over:**
 *   - **Zoom-to-cursor and the clamp genuinely conflict at the bound.** The wheel writer keeps the
 *     world point under the pointer fixed by solving for a `view` that does exactly that; clamping
 *     the result breaks that anchor whenever the unclamped request lands out of bounds, so the zoom
 *     visibly "slides" a little at the edge. That is correct, not a defect — don't rubber-band or
 *     special-case it away.
 *   - **Drawing just past the board edge stays reachable.** Annotation points are deliberately never
 *     spatially clamped — only the CAMERA is bounded here, not where a stroke may land. A user can
 *     still pan to the edge and draw slightly outside the board.
 *
 * **The degenerate zero-viewport case.** `viewport = { width: 0, height: 0 }` is real, not
 * hypothetical — it's `CombatStage`'s own initial `size` state before the `ResizeObserver` fires its
 * first measurement. With `viewport.width/height` both 0, the high end of each interval collapses to
 * 0 (`viewport.width / 2`) while the low end stays `-board.width * view.scale` (resp. height) — a
 * non-empty `[-board.width * scale, 0]` range, not a single point — so the clamp still returns a
 * well-defined, finite view. No NaN, no throw.
 */

export type View = { x: number; y: number; scale: number };
export type Viewport = { width: number; height: number };
export type Board = { width: number; height: number };

/** Clamp `lo <= n <= hi`, tolerating an inverted range (returns `hi` — never NaN, never a crash). */
const clampInterval = (n: number, lo: number, hi: number): number => Math.min(Math.max(n, lo), hi);

/**
 * Clamp `view` so the viewport's centre point (in world coordinates) lies inside `board`'s rect.
 * `scale` passes through unchanged — the caller owns zoom clamping. See the module header for the
 * derivation and the two deliberate non-smoothings (zoom-to-cursor edge slide, unclamped annotation
 * points).
 */
export function clampView(view: View, viewport: Viewport, board: Board): View {
  const xLo = viewport.width / 2 - board.width * view.scale;
  const xHi = viewport.width / 2;
  const yLo = viewport.height / 2 - board.height * view.scale;
  const yHi = viewport.height / 2;
  return {
    scale: view.scale,
    x: clampInterval(view.x, xLo, xHi),
    y: clampInterval(view.y, yLo, yHi),
  };
}
